import React, { useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { useTranslation } from "react-i18next";
import { AuthProvider, useAuth } from "./AuthContext";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DistributorLoginPage from "./pages/DistributorLoginPage";
import RegisterPage from "./pages/RegisterPage";
import AdminDashboard from "./pages/AdminDashboard";
import InventoryPage from "./pages/InventoryPage";
import AddProductPage from "./pages/AddProductPage";
import DistributorsPage from "./pages/DistributorsPage";
import DistributorDetailPage from "./pages/DistributorDetailPage";
import DistributorDashboard from "./pages/DistributorDashboard";
import IntakePage from "./pages/IntakePage";
import UsersPage from "./pages/UsersPage";
import UserAuditPage from "./pages/UserAuditPage";
import MovementLogPage from "./pages/MovementLogPage";
import ReconciliationPage from "./pages/ReconciliationPage";
import SettingsPage from "./pages/SettingsPage";
import SuperOwnersPage from "./pages/SuperOwnersPage";
import SalesHistoryPage from "./pages/SalesHistoryPage";
import SettlementsPage from "./pages/SettlementsPage";
import TrashPage from "./pages/TrashPage";
import ExpensesPage from "./pages/ExpensesPage";
import ReceivablesPage from "./pages/ReceivablesPage";
import ProductionPage from "./pages/ProductionPage";
import MarginSettlementsPage from "./pages/MarginSettlementsPage";
import PricingAuditPage from "./pages/PricingAuditPage";
import StockCountPage from "./pages/StockCountPage";
import PortalPage from "./pages/PortalPage";
import StorePage from "./pages/StorePage";
import StoreOrdersPage from "./pages/StoreOrdersPage";
import CustomersPage from "./pages/CustomersPage";
import MyStorePage from "./pages/MyStorePage";
import StoresPage from "./pages/StoresPage";
import PriceSheetsPage from "./pages/PriceSheetsPage";
import ProductVideosPage from "./pages/ProductVideosPage";
import VideoLibraryPage from "./pages/VideoLibraryPage";
import StoreVideosPage from "./pages/StoreVideosPage";
import { BrutalButton } from "./components/Brutal";
import LanguageToggle from "./components/LanguageToggle";
import { VersionBadge } from "./components/VersionBadge";
import { InstallPWAButton } from "./components/InstallPWAButton";
import { SignOut, Package, Users, House, UploadSimple, List, X, UsersThree, ArrowsLeftRight, Trash, Receipt, CurrencyDollar, Factory, Scales, Tag, Storefront, FilmSlate } from "@phosphor-icons/react";
import "./App.css";

function TopBar() {
  const { user, logout, isElevated, isOwner, isSuperOwner, mustChangePassword, canSeeCost } = useAuth();
  const { t } = useTranslation();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  if (!user || user === false) return null;

  const elevatedNav = [
    { to: "/", label: t("nav.dashboard"), icon: House, key: "dashboard" },
  ];
  if (!user.block_master_inventory) elevatedNav.push({ to: "/inventory", label: t("nav.inventory"), icon: Package, key: "inventory" });
  if (isOwner || isSuperOwner) elevatedNav.push({ to: "/inventory/intake", label: t("nav.add_stock"), icon: UploadSimple, key: "add-stock" });
  // Partners hold consigned stock of their own — one-tap access to their page.
  if (user.role === "partner") elevatedNav.push({ to: `/distributors/${user.id}`, label: t("nav.my_held_stock"), icon: Package, key: "my-held-stock" });
  // v405.15 — every code-holder manages their public retail store.
  if (user.distributor_code) elevatedNav.push({ to: "/my-store", label: t("nav.my_store"), icon: Storefront, key: "my-store" });
  // v406.1 — org-wide store directory (partners: their subtree only).
  if (["owner", "admin", "cfo", "partner"].includes(user.role)) elevatedNav.push({ to: "/stores", label: t("nav.stores"), icon: Storefront, key: "stores" });
  elevatedNav.push({ to: "/distributors", label: t("nav.distributors"), icon: Users, key: "distributors" });
  elevatedNav.push({ to: "/videos", label: t("nav.videos"), icon: FilmSlate, key: "videos" });
  elevatedNav.push({ to: "/video-library", label: t("nav.video_guides"), icon: FilmSlate, key: "video-library" });
  elevatedNav.push({ to: "/movement-log", label: t("nav.movement_log"), icon: ArrowsLeftRight, key: "movement-log" });
  elevatedNav.push({ to: "/sales-history", label: t("nav.sales_history"), icon: Receipt, key: "sales-history" });
  elevatedNav.push({ to: "/production", label: t("nav.production"), icon: Factory, key: "production" });
  elevatedNav.push({ to: "/margins", label: t("nav.margins"), icon: Scales, key: "margins" });
  if (isOwner && canSeeCost) elevatedNav.push({ to: "/pricing-audit", label: t("nav.pricing_audit"), icon: Tag, key: "pricing-audit" });
  if (isOwner) elevatedNav.push({ to: "/receivables", label: t("nav.receivables"), icon: CurrencyDollar, key: "receivables" });
  if (isOwner || user.can_reconcile || isSuperOwner) elevatedNav.push({ to: "/settlements", label: t("nav.settlements"), icon: CurrencyDollar, key: "settlements" });
  if (isOwner || user.can_reconcile || isSuperOwner) elevatedNav.push({ to: "/store-orders", label: t("nav.store_orders"), icon: Storefront, key: "store-orders" });
  if (isOwner || user.can_reconcile || isSuperOwner) elevatedNav.push({ to: "/customers", label: t("nav.customers"), icon: Users, key: "customers" });
  if (!user.block_expenses) elevatedNav.push({ to: "/expenses", label: t("nav.expenses"), icon: Receipt, key: "expenses" });
  if (isOwner) elevatedNav.push({ to: "/users", label: t("nav.users"), icon: UsersThree, key: "users" });
  if (isOwner) elevatedNav.push({ to: "/trash", label: t("nav.trash"), icon: Trash, key: "trash" });

  // While the user is locked into the force-rotate flow, hide every nav link.
  const nav = mustChangePassword
    ? []
    : isElevated
      ? elevatedNav
      : [
        { to: "/", label: t("nav.my_stock"), icon: Package, key: "my-stock" },
        { to: "/my-store", label: t("nav.my_store"), icon: Storefront, key: "my-store" },
        { to: "/videos", label: t("nav.product_videos"), icon: FilmSlate, key: "videos" },
        { to: "/video-library", label: t("nav.video_guides"), icon: FilmSlate, key: "video-library" },
        { to: "/store-orders", label: t("nav.store_orders"), icon: Receipt, key: "store-orders" },
        { to: "/customers", label: t("nav.customers"), icon: Users, key: "customers" },
        { to: "/distributors", label: t("nav.my_distributors"), icon: Users, key: "my-distributors" },
        { to: "/movement-log", label: t("nav.movement_log"), icon: ArrowsLeftRight, key: "movement-log" },
        { to: "/sales-history", label: t("nav.sales_history"), icon: Receipt, key: "sales-history" },
        { to: "/expenses", label: t("nav.expenses"), icon: Receipt, key: "expenses" },
      ];

  return (
    <header
      className="sticky top-0 z-40 w-full bg-white/85 backdrop-blur-xl border-b border-zinc-200"
      data-testid="app-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2.5 min-w-0" data-testid="logo-link" onClick={() => setOpen(false)}>
          <div className="w-7 h-7 bg-zinc-950 text-white rounded-md flex items-center justify-center font-bold text-[11px] shrink-0">
            RU
          </div>
          <span className="font-semibold text-[15px] text-zinc-950 truncate">ReUP</span>
          <VersionBadge className="hidden sm:inline" />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {nav.map((n) => {
            const active = loc.pathname === n.to;
            const Icon = n.icon;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.key}`}
                className={`flex items-center gap-2 px-3 h-9 rounded-md text-sm font-medium transition-colors ${
                  active
                    ? "bg-zinc-100 text-zinc-950"
                    : "text-zinc-600 hover:text-zinc-950 hover:bg-zinc-50"
                }`}
              >
                <Icon size={16} weight="bold" />
                <span>{n.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <InstallPWAButton className="!px-2 sm:!px-3" />
          <LanguageToggle className="hidden sm:inline-flex" />
          <Link
            to="/settings/password"
            className="hidden sm:flex flex-col items-end leading-tight px-2 py-1 rounded-md hover:bg-zinc-50 transition-colors"
            data-testid="user-settings-link"
            title={t("settings.title")}
          >
            <span className="text-[11px] text-zinc-500 font-medium">{t(`roles.${user.role}`, user.role)}</span>
            <span className="text-[13px] text-zinc-900 font-medium truncate max-w-[140px] lg:max-w-[160px]">{user.name}</span>
          </Link>
          <BrutalButton
            variant="secondary"
            size="md"
            onClick={logout}
            data-testid="logout-button"
            className="hidden sm:inline-flex"
          >
            <SignOut size={15} weight="bold" /> {t("auth.log_out")}
          </BrutalButton>
          <button
            className="md:hidden p-2 -mr-2 rounded-md text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={20} weight="bold" /> : <List size={20} weight="bold" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden border-t border-zinc-200 bg-white" data-testid="mobile-menu">
          <nav className="px-2 py-2 flex flex-col">
            {nav.map((n) => {
              const active = loc.pathname === n.to;
              const Icon = n.icon;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${n.key}`}
                  className={`flex items-center gap-3 px-3 h-11 rounded-md text-sm font-medium ${
                    active ? "bg-zinc-100 text-zinc-950" : "text-zinc-700 hover:bg-zinc-50"
                  }`}
                >
                  <Icon size={18} weight="bold" />
                  <span>{n.label}</span>
                </Link>
              );
            })}
            <div className="border-t border-zinc-100 mt-2 pt-2 px-3 pb-3 flex flex-wrap items-center justify-between gap-2">
              <div className="leading-tight">
                <div className="text-[11px] text-zinc-500 font-medium uppercase tracking-wide">{t(`roles.${user.role}`, user.role)}</div>
                <div className="text-sm font-medium text-zinc-900 truncate">{user.name} <VersionBadge /></div>
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <Link
                  to="/settings/password"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-settings-link"
                  className="text-xs font-medium text-zinc-700 hover:text-zinc-950 underline-offset-2 hover:underline"
                >
                  {t("settings.change_password")}
                </Link>
                <LanguageToggle />
                <BrutalButton
                  variant="secondary"
                  size="sm"
                  onClick={() => { setOpen(false); logout(); }}
                  data-testid="mobile-logout-button"
                >
                  <SignOut size={14} weight="bold" /> {t("auth.log_out")}
                </BrutalButton>
              </div>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}

function Protected({ children, roles, flagAllow, flagDeny }) {
  const { user, mustChangePassword, isSuperOwner } = useAuth();
  const { t } = useTranslation();
  const loc = useLocation();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-sm text-zinc-500">{t("common.loading")}</div>
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace state={{ from: loc.pathname + loc.search }} />;
  // Force first-login password rotation. The only route they can hit is /settings/password.
  if (mustChangePassword && loc.pathname !== "/settings/password") {
    return <Navigate to="/settings/password" replace />;
  }
  // v402.0 per-user gates: flagDeny hides a section (e.g. Jason Ross —
  // block_master_inventory / block_expenses); flagAllow grants beyond roles
  // (e.g. Sukhinder — can_reconcile on Settlements).
  if (flagDeny && user[flagDeny]) return <Navigate to="/" replace />;
  // v404.8 Moin-parity: allowlisted super-owners (Harvey/Sukhinder) pass every
  // role gate. flagDeny above still wins.
  if (roles && !roles.includes(user.role) && !(flagAllow && user[flagAllow]) && !isSuperOwner) return <Navigate to="/" replace />;
  return children;
}

function RoleHome() {
  const { user, isElevated } = useAuth();
  if (user === null) return null;
  if (user === false) return <Navigate to="/login" replace />;
  if (isElevated) return <AdminDashboard />;
  return <DistributorDashboard />;
}

/** v405.15b — legacy /store/{code} links (early QRs) redirect to /{code}. */
function StoreRedirect() {
  const { code } = useParams();
  return <Navigate to={`/${code}`} replace />;
}

// Branded partner-portal home: the storefront deep-links here. Unauth users
// land on the AXIS-themed /distributor/login; elevated staff go to admin home.
function DistributorPortalHome() {
  const { user, isElevated, mustChangePassword } = useAuth();
  if (user === null) return null;
  if (user === false) return <Navigate to="/distributor/login" replace />;
  if (mustChangePassword) return <Navigate to="/settings/password" replace />;
  if (isElevated) return <Navigate to="/" replace />;
  return <DistributorDashboard />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster richColors position="top-center" toastOptions={{ className: "!font-sans" }} />
        <div className="min-h-screen bg-white">
          <TopBar />
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
            <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/distributor/login" element={<DistributorLoginPage />} />
            <Route path="/distributor/dashboard" element={<DistributorPortalHome />} />
            <Route path="/register/:token" element={<RegisterPage />} />
            <Route path="/portal" element={<PortalPage />} />
            <Route path="/portal/:code" element={<PortalPage />} />
            {/* v405.15b — stores live at the ROOT: reupclub.com/ABRV1 */}
            <Route path="/store/:code" element={<StoreRedirect />} />
            <Route path="/my-store" element={<Protected roles={["owner", "admin", "cfo", "partner", "distributor"]}><MyStorePage /></Protected>} />
            <Route path="/stores" element={<Protected roles={["owner", "admin", "cfo", "partner"]}><StoresPage /></Protected>} />
            <Route path="/price-sheets" element={<Protected roles={["owner", "admin", "cfo"]}><PriceSheetsPage /></Protected>} />
            <Route path="/" element={<Protected><RoleHome /></Protected>} />
            <Route path="/inventory" element={<Protected roles={["owner", "admin", "cfo", "partner"]} flagDeny="block_master_inventory"><InventoryPage /></Protected>} />
            <Route path="/inventory/new" element={<Protected roles={["owner", "admin", "cfo"]}><AddProductPage /></Protected>} />
            <Route path="/inventory/intake" element={<Protected roles={["owner", "admin", "cfo"]}><IntakePage /></Protected>} />
            <Route path="/distributors" element={<Protected><DistributorsPage /></Protected>} />
            <Route path="/distributors/:id" element={<Protected><DistributorDetailPage /></Protected>} />
            <Route path="/users" element={<Protected roles={["owner", "admin"]}><UsersPage /></Protected>} />
            <Route path="/user-audit" element={<Protected roles={["owner", "admin", "cfo"]}><UserAuditPage /></Protected>} />
            <Route path="/movement-log" element={<Protected><MovementLogPage /></Protected>} />
            <Route path="/reconciliation" element={<Protected roles={["owner", "admin", "cfo"]}><ReconciliationPage /></Protected>} />
            <Route path="/settings/password" element={<Protected><SettingsPage /></Protected>} />
            <Route path="/settings/super-owners" element={<Protected roles={["owner", "admin"]}><SuperOwnersPage /></Protected>} />
            <Route path="/trash" element={<Protected roles={["owner", "admin"]}><TrashPage /></Protected>} />
            <Route path="/receivables" element={<Protected roles={["owner", "admin", "cfo"]}><ReceivablesPage /></Protected>} />
            <Route path="/expenses" element={<Protected flagDeny="block_expenses"><ExpensesPage /></Protected>} />
            <Route path="/sales-history" element={<Protected><SalesHistoryPage /></Protected>} />
            <Route path="/production" element={<Protected roles={["owner", "admin", "cfo", "partner"]}><ProductionPage /></Protected>} />
            <Route path="/margins" element={<Protected roles={["owner", "admin", "cfo", "partner"]}><MarginSettlementsPage /></Protected>} />
            <Route path="/pricing-audit" element={<Protected roles={["owner", "admin"]}><PricingAuditPage /></Protected>} />
            <Route path="/stock-count" element={<Protected roles={["owner", "admin"]}><StockCountPage /></Protected>} />
            <Route path="/settlements" element={<Protected roles={["owner", "admin", "cfo"]} flagAllow="can_reconcile"><SettlementsPage /></Protected>} />
            <Route path="/store-orders" element={<Protected roles={["owner", "admin", "cfo", "distributor"]} flagAllow="can_reconcile"><StoreOrdersPage /></Protected>} />
            <Route path="/customers" element={<Protected><CustomersPage /></Protected>} />
            {/* v414.17 — must be registered BEFORE the /:code store catch-all */}
            <Route path="/videos" element={<Protected><ProductVideosPage /></Protected>} />
            {/* v414.80 — curated Video Library: in-app + public storefront */}
            <Route path="/video-library" element={<Protected><VideoLibraryPage /></Protected>} />
            <Route path="/:code/videos" element={<StoreVideosPage />} />
              {/* v405.15b — root-level distributor store: any unmatched single
                  segment resolves as a store code (reupclub.com/ABRV1).
                  StorePage renders a branded not-found for unknown codes. */}
              <Route path="/:code" element={<StorePage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}
